import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, RotateCcw, Sparkles, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import {
  areaResults,
  isAiQuestion,
  loadSelfAssessmentCatalog,
  type SelfAssessmentCatalog,
} from '@/lib/self-assessment'
import type { EvaluationCriterion, SelfAssessment, SelfAssessmentScore, TeamMember } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { ScaleLegend } from '@/components/scale-legend'
import { NoteField, ScoreButtons, answerLabel } from '@/components/criterion-scoring'
import { SelfAssessmentResults } from '@/components/self-assessment-results'

/**
 * /self-assessment/:id — take one, or read one back.
 *
 * Who gets which mode is decided by ownership, and the database agrees: only
 * the owner can write (0067), so a coach opening someone's run gets the
 * read-only results and their answers, never the scoring buttons. A coach who
 * forced a write would get a 403 from the policy, not a quiet success.
 */
export function SelfAssessmentDetailPage() {
  const { id = '' } = useParams()
  const { session } = useAuth()
  const userId = session?.user?.id
  const navigate = useNavigate()
  const [busy, setBusy] = React.useState(false)

  const { data, loading, error, refetch, setData } = useQuery(() => loadRun(id), [id])

  if (loading && !data) return <Loading />
  if (error) return <ErrorNote error={error} />
  if (!data) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title="Not found"
        description="This self-assessment does not exist, or it is not yours to see."
      />
    )
  }

  const { assessment, catalog } = data
  const mine = assessment.user_id === userId
  const editable = mine && assessment.status === 'in_progress'
  // Only one open run per person (0067's partial unique index), so a finished
  // run cannot be reopened while a retake is under way.
  const canReopen = mine && assessment.status === 'complete' && !data.otherOpen
  const answered = data.scores.length
  const total = catalog.criteria.length
  const who = mine ? 'Your' : `${data.member?.display_name || data.member?.email || 'Their'}’s`

  /** Optimistic: the button lights at once, the write follows. */
  async function answer(c: EvaluationCriterion, score: number, notes?: string | null) {
    const existing = data!.scores.find((s) => s.criterion_id === c.id)
    const nextNotes = notes !== undefined ? notes || null : existing?.notes ?? null
    setData((d) =>
      d && {
        ...d,
        scores: [
          ...d.scores.filter((s) => s.criterion_id !== c.id),
          {
            id: existing?.id ?? `pending-${c.id}`,
            assessment_id: id,
            criterion_id: c.id,
            team_id: d.assessment.team_id,
            user_id: d.assessment.user_id,
            score,
            notes: nextNotes,
          },
        ],
      },
    )
    const { error: err } = await sb()
      .from('self_assessment_scores')
      .upsert(
        { assessment_id: id, criterion_id: c.id, score, notes: nextNotes },
        { onConflict: 'assessment_id,criterion_id' },
      )
    if (err) {
      window.alert(err.message)
      void refetch()
    }
  }

  async function setStatus(status: SelfAssessment['status']) {
    setBusy(true)
    const { error: err } = await sb().from('self_assessments').update({ status }).eq('id', id)
    setBusy(false)
    if (err) window.alert(err.message)
    else void refetch()
  }

  async function remove() {
    if (!window.confirm('Delete this self-assessment and every answer in it?')) return
    const { error: err } = await sb().from('self_assessments').delete().eq('id', id)
    if (err) window.alert(err.message)
    else navigate('/self-assessment')
  }

  return (
    <Page
      title={`${who} self-assessment`}
      description={
        assessment.status === 'complete'
          ? `Finished ${formatDate(assessment.completed_at)}`
          : `Started ${formatDate(assessment.started_at)} · ${answered} of ${total} answered`
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={mine ? '/self-assessment' : '/self-assessment/team'}>
              <ArrowLeft /> Back
            </Link>
          </Button>
          {canReopen && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void setStatus('in_progress')}>
              <RotateCcw /> Reopen
            </Button>
          )}
          {mine && (
            <Button variant="ghost" size="sm" onClick={() => void remove()}>
              <Trash2 /> Delete
            </Button>
          )}
        </div>
      }
    >
      {editable ? (
        <TakeView
          catalog={catalog}
          scores={data.scores}
          busy={busy}
          onAnswer={(c, v, notes) => void answer(c, v, notes)}
          onFinish={() => void setStatus('complete')}
        />
      ) : (
        <ReadView data={data} />
      )}
    </Page>
  )
}

type RunData = NonNullable<Awaited<ReturnType<typeof loadRun>>>

/** One run, its catalog, its answers, whose it is, and the run before it. */
async function loadRun(id: string) {
  const run = await sb().from('self_assessments').select('*').eq('id', id).maybeSingle()
  if (run.error) throw run.error
  if (!run.data) return null
  const assessment = run.data as SelfAssessment
  const same = () =>
    sb()
      .from('self_assessments')
      .select('*')
      .eq('team_id', assessment.team_id)
      .eq('user_id', assessment.user_id)
      .eq('template_id', assessment.template_id)
  const [catalog, scores, member, previous, open] = await Promise.all([
    loadSelfAssessmentCatalog(),
    sb().from('self_assessment_scores').select('*').eq('assessment_id', id),
    sb()
      .from('team_members')
      .select('*')
      .eq('team_id', assessment.team_id)
      .eq('user_id', assessment.user_id)
      .maybeSingle(),
    // The finished run before this one, for "what moved".
    same()
      .eq('status', 'complete')
      .lt('completed_at', assessment.completed_at ?? '9999-12-31')
      .order('completed_at', { ascending: false })
      .limit(1),
    same().eq('status', 'in_progress').neq('id', id).limit(1),
  ])
  for (const r of [scores, previous, open]) if (r.error) throw r.error
  const prev = (previous.data ?? [])[0] as SelfAssessment | undefined
  const prevScores = prev
    ? await sb().from('self_assessment_scores').select('*').eq('assessment_id', prev.id)
    : { data: [], error: null }
  if (prevScores.error) throw prevScores.error
  return {
    assessment,
    catalog: catalog!,
    scores: (scores.data ?? []) as SelfAssessmentScore[],
    member: (member.data ?? null) as TeamMember | null,
    previous: prev ?? null,
    previousScores: (prevScores.data ?? []) as SelfAssessmentScore[],
    otherOpen: (open.data ?? []).length > 0,
  }
}

/** Answering: every area, every question, a sticky progress bar and Finish. */
function TakeView({
  catalog,
  scores,
  busy,
  onAnswer,
  onFinish,
}: {
  catalog: SelfAssessmentCatalog
  scores: SelfAssessmentScore[]
  busy: boolean
  onAnswer: (c: EvaluationCriterion, score: number, notes?: string | null) => void
  onFinish: () => void
}) {
  const answered = scores.length
  const total = catalog.criteria.length
  const remaining = total - answered
  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 -mx-1 space-y-2 bg-background/95 px-1 py-2 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${total ? Math.round((answered / total) * 100) : 0}%` }}
            />
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {answered} of {total}
          </span>
          <Button size="sm" disabled={busy || remaining > 0} onClick={onFinish}>
            <Check /> Finish
          </Button>
        </div>
        <ScaleLegend template={catalog.template} />
      </div>

      <p className="text-sm text-muted-foreground">
        Rate yourself as you are today, not as you would like to be. The questions under each line
        are there to jog a real example.
      </p>

      {catalog.categories.map((cat) => (
        <Card key={cat.id}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{cat.name}</CardTitle>
            {cat.description && <p className="text-xs text-muted-foreground">{cat.description}</p>}
          </CardHeader>
          <CardContent className="space-y-5">
            {catalog.criteria
              .filter((c) => c.category_id === cat.id)
              .map((c) => {
                const current = scores.find((s) => s.criterion_id === c.id)
                const value = current != null ? Number(current.score) : null
                return (
                  <div key={c.id}>
                    <p className="flex items-start gap-1.5 text-sm">
                      {isAiQuestion(c) && (
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-label="AI question" />
                      )}
                      {c.name}
                    </p>
                    {c.prompt && <p className="mt-0.5 text-xs text-muted-foreground">{c.prompt}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <ScoreButtons
                        template={catalog.template}
                        criterion={c}
                        value={value}
                        onPick={(v) => onAnswer(c, v)}
                      />
                      {value != null && (
                        <span className="ml-1.5 text-xs font-medium">
                          {answerLabel(catalog.template, c, value)}
                        </span>
                      )}
                    </div>
                    {current && (
                      <NoteField
                        key={`${c.id}-note`}
                        initial={current.notes ?? ''}
                        placeholder="An example, or what you'd like to get better at (optional)"
                        onCommit={(notes) => onAnswer(c, Number(current.score), notes)}
                      />
                    )}
                  </div>
                )
              })}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button disabled={busy || remaining > 0} onClick={onFinish}>
          <Check /> {remaining > 0 ? `Answer ${remaining} more to finish` : 'Finish'}
        </Button>
      </div>
    </div>
  )
}

/** Reading back: the results (once finished), then every answer and its note. */
function ReadView({ data }: { data: RunData }) {
  const { assessment, catalog } = data
  const results = areaResults(catalog, data.scores)
  return (
    <div className="space-y-6">
      {assessment.status === 'complete' ? (
        <SelfAssessmentResults
          catalog={catalog}
          results={results}
          previous={data.previous ? areaResults(catalog, data.previousScores) : null}
          nowLabel={formatDate(assessment.completed_at)}
          previousLabel={data.previous ? formatDate(data.previous.completed_at) : undefined}
        />
      ) : (
        <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
          Still in progress — {data.scores.length} of {catalog.criteria.length} answered. Results
          appear when it is finished.
        </p>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Answers</h2>
        {results.map((r) => (
          <Card key={r.category.id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{r.category.name}</CardTitle>
            </CardHeader>
            <CardContent className="divide-y p-0">
              {catalog.criteria
                .filter((c) => c.category_id === r.category.id)
                .map((c) => {
                  const s = data.scores.find((x) => x.criterion_id === c.id)
                  return (
                    <div key={c.id} className="flex gap-3 px-4 py-2 text-sm">
                      <span className="w-6 shrink-0 text-right font-semibold tabular-nums">
                        {s != null ? Number(s.score) : '–'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p>{c.name}</p>
                        {s?.notes && <p className="mt-0.5 text-xs text-muted-foreground">{s.notes}</p>}
                      </div>
                    </div>
                  )
                })}
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  )
}
